import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';
import { 
  Calendar, Ship, ChevronLeft, ChevronRight, Save, Plus, 
  AlertTriangle, CheckCircle2, Clock, Play, Eye, FileText,
  Factory, Loader2, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const STATUS_CONFIG = {
  planejado: { label: 'Planejado', color: 'bg-gray-100 text-gray-700 border-gray-300', barColor: 'bg-gray-400', icon: Clock },
  em_andamento: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700 border-blue-300', barColor: 'bg-blue-500', icon: Play },
  concluido: { label: 'Concluído', color: 'bg-green-100 text-green-700 border-green-300', barColor: 'bg-green-500', icon: CheckCircle2 },
  atrasado: { label: 'Atrasado', color: 'bg-red-100 text-red-700 border-red-300', barColor: 'bg-red-500', icon: AlertTriangle }
};

const FILTER_OPTIONS = {
  diario: { label: 'Diário', days: 7 },
  semanal: { label: 'Semanal', days: 28 },
  mensal: { label: 'Mensal', days: 90 },
  personalizado: { label: 'Personalizado', days: null }
};

// Helper para formatar data
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

// Helper para calcular status baseado em datas
const calculateStatus = (item) => {
  if (item.status === 'concluido') return 'concluido';
  if (!item.data_fim) return 'planejado';
  
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dataFim = new Date(item.data_fim);
  dataFim.setHours(0, 0, 0, 0);
  
  if (item.status === 'em_andamento') {
    return dataFim < hoje ? 'atrasado' : 'em_andamento';
  }
  
  if (item.data_inicio) {
    const dataInicio = new Date(item.data_inicio);
    dataInicio.setHours(0, 0, 0, 0);
    if (dataInicio <= hoje && item.status === 'planejado') {
      return dataFim < hoje ? 'atrasado' : 'planejado';
    }
  }
  
  return item.status || 'planejado';
};

const Cronograma = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [factories, setFactories] = useState([]);
  const [processTemplates, setProcessTemplates] = useState([]);
  const [vesselModels, setVesselModels] = useState([]);
  const [etapasMacro, setEtapasMacro] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedEmbarcacao, setSelectedEmbarcacao] = useState(0);
  const [cronograma, setCronograma] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(false);
  
  // Ref para auto-save com debounce
  const autoSaveTimeoutRef = useRef(null);
  const lastSavedCronogramaRef = useRef(null);
  
  // Filtro de visualização
  const [viewFilter, setViewFilter] = useState('semanal');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  
  // Range de datas visíveis
  const [viewStartDate, setViewStartDate] = useState(new Date());
  const [viewEndDate, setViewEndDate] = useState(new Date());

  useEffect(() => {
    document.title = "Cronograma — NautilusOS";
    fetchInitialData();
    updateViewDates(viewFilter);
  }, []);

  useEffect(() => {
    if (selectedProject && selectedEmbarcacao !== null) {
      fetchOrCreateCronograma();
    }
  }, [selectedProject, selectedEmbarcacao]);

  useEffect(() => {
    if (viewFilter !== 'personalizado') {
      updateViewDates(viewFilter);
    }
  }, [viewFilter]);

  // Auto-save com debounce quando cronograma muda
  useEffect(() => {
    if (!cronograma || !pendingChanges) return;
    
    // Limpar timeout anterior
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Agendar auto-save após 2 segundos de inatividade
    autoSaveTimeoutRef.current = setTimeout(async () => {
      // Verificar se realmente houve mudança
      if (JSON.stringify(cronograma) !== JSON.stringify(lastSavedCronogramaRef.current)) {
        try {
          await api.post('/cronogramas', cronograma);
          lastSavedCronogramaRef.current = JSON.parse(JSON.stringify(cronograma));
          setPendingChanges(false);
          toast.success('Alterações salvas automaticamente!', { duration: 2000 });
        } catch (error) {
          console.error('Erro no auto-save:', error);
          toast.error('Erro ao salvar automaticamente. Clique em Salvar.');
        }
      }
    }, 2000);
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [cronograma, pendingChanges]);

  const updateViewDates = (filter) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    // Início sempre começa no domingo da semana atual
    const start = new Date(hoje);
    start.setDate(hoje.getDate() - hoje.getDay());
    
    const days = FILTER_OPTIONS[filter]?.days || 30;
    const end = new Date(start);
    end.setDate(start.getDate() + days);
    
    setViewStartDate(start);
    setViewEndDate(end);
  };

  const handleCustomDateChange = () => {
    if (customDateStart && customDateEnd) {
      setViewStartDate(new Date(customDateStart));
      setViewEndDate(new Date(customDateEnd));
    }
  };

  const fetchInitialData = async () => {
    try {
      // Carregar dados principais
      const [projectsRes, factoriesRes, templatesRes, modelsRes] = await Promise.all([
        api.get('/projects'),
        api.get('/factories'),
        api.get('/process-templates'),
        api.get('/vessel-models')
      ]);
      
      setProjects(projectsRes.data || []);
      setFactories(factoriesRes.data || []);
      setProcessTemplates(templatesRes.data || []);
      setVesselModels(modelsRes.data || []);
      
      // Selecionar primeiro projeto por padrão
      if (projectsRes.data?.length > 0) {
        setSelectedProject(projectsRes.data[0]);
      }
      
      // Carregar etapas macro separadamente (não crítico)
      try {
        const etapasMacroRes = await api.get('/etapas-macro');
        setEtapasMacro(etapasMacroRes.data || []);
      } catch (etapasError) {
        // Usar valores padrão se falhar — silent fallback
        setEtapasMacro([
          { numero: 1, nome: 'Projeto e Planejamento' },
          { numero: 2, nome: 'Produção e Laminação' },
          { numero: 3, nome: 'Acabamentos' },
          { numero: 4, nome: 'Entrega' }
        ]);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrCreateCronograma = async () => {
    if (!selectedProject) return;
    
    try {
      const response = await api.get(`/cronogramas/${selectedProject.id}/${selectedEmbarcacao}`);
      
      if (response.data && response.data.itens?.length > 0) {
        setCronograma(response.data);
        lastSavedCronogramaRef.current = JSON.parse(JSON.stringify(response.data));
        setPendingChanges(false);
      } else {
        // Criar cronograma automaticamente baseado no modelo de embarcação
        await initializeCronograma();
      }
    } catch (error) {
      // Se não existe, criar um novo
      await initializeCronograma();
    }
  };

  const initializeCronograma = async () => {
    if (!selectedProject) return;
    
    const embarcacao = selectedProject.embarcacoes?.[selectedEmbarcacao];
    if (!embarcacao) {
      toast.error('Embarcação não encontrada');
      return;
    }
    
    // Buscar modelo de embarcação
    const modelo = vesselModels.find(m => m.id === embarcacao.vessel_model_id);
    
    // Obter fases aplicáveis do modelo ou todas as fases se não houver modelo
    let fasesAplicaveis = [];
    
    if (modelo && modelo.fases_aplicaveis?.length > 0) {
      // Filtrar apenas as fases aplicáveis ao modelo
      fasesAplicaveis = processTemplates
        .filter(pt => modelo.fases_aplicaveis.includes(pt.id))
        .sort((a, b) => (a.etapa_macro - b.etapa_macro) || (a.order - b.order));
    } else {
      // Se não houver modelo ou fases definidas, usar todas as fases
      fasesAplicaveis = [...processTemplates].sort((a, b) => 
        (a.etapa_macro - b.etapa_macro) || (a.order - b.order)
      );
    }
    
    // Criar itens do cronograma baseados nas fases
    const itens = fasesAplicaveis.map((fase, index) => ({
      id: crypto.randomUUID(),
      fase_id: fase.id,
      fase_nome: fase.name,
      fase_descricao: fase.description || '',
      etapa_macro: fase.etapa_macro,
      etapa_macro_nome: fase.etapa_macro_nome,
      duracao_estimada_horas: fase.duration_hours || 0,
      etapas: fase.steps?.map(step => ({
        id: step.id || crypto.randomUUID(),
        nome: step.name,
        descricao: step.description || '',
        concluida: false
      })) || [],
      fabrica_id: '',
      fabrica_nome: '',
      data_inicio: '',
      data_fim: '',
      status: 'planejado',
      progresso: 0,
      responsavel: '',
      observacoes: '',
      ordem: index + 1
    }));
    
    const novoCronograma = {
      projeto_id: selectedProject.id,
      embarcacao_index: selectedEmbarcacao,
      itens: itens
    };
    
    try {
      const response = await api.post('/cronogramas', novoCronograma);
      setCronograma(response.data);
      lastSavedCronogramaRef.current = JSON.parse(JSON.stringify(response.data));
      setPendingChanges(false);
      toast.success(`Cronograma inicializado com ${itens.length} fases!`);
    } catch (error) {
      console.error('Erro ao criar cronograma:', error);
      toast.error('Erro ao criar cronograma');
    }
  };

  /**
   * Calcula diff entre o cronograma atual e o modelo da embarcação.
   * Retorna fases novas (no modelo, faltam no cronograma) e órfãs (no cronograma, removidas do modelo).
   */
  const diffCronogramaModelo = useMemo(() => {
    if (!cronograma || !cronograma.itens || !selectedProject) return null;
    const embarcacao = selectedProject.embarcacoes?.[selectedEmbarcacao];
    if (!embarcacao) return null;
    const modelo = vesselModels.find(m => m.id === embarcacao.vessel_model_id);
    if (!modelo || !modelo.fases_aplicaveis?.length) return null;

    const idsModelo = new Set(modelo.fases_aplicaveis);
    const idsCronograma = new Set((cronograma.itens || []).map(i => i.fase_id).filter(Boolean));

    const fasesNovas = processTemplates
      .filter(pt => idsModelo.has(pt.id) && !idsCronograma.has(pt.id));
    const fasesOrfas = (cronograma.itens || [])
      .filter(i => i.fase_id && !idsModelo.has(i.fase_id));

    // Detectar mudanças de nome/etapa_macro nos itens existentes
    const fasesDessincronizadas = (cronograma.itens || []).filter(i => {
      if (!i.fase_id) return false;
      const pt = processTemplates.find(p => p.id === i.fase_id);
      if (!pt) return false;
      return (pt.name !== i.fase_nome) || (pt.etapa_macro !== i.etapa_macro);
    });

    return {
      precisaSincronizar: fasesNovas.length > 0 || fasesOrfas.length > 0 || fasesDessincronizadas.length > 0,
      fasesNovas,
      fasesOrfas,
      fasesDessincronizadas,
      totalModelo: modelo.fases_aplicaveis.length,
      totalCronograma: cronograma.itens.length,
    };
  }, [cronograma, selectedProject, selectedEmbarcacao, vesselModels, processTemplates]);

  /**
   * Sincroniza o cronograma com o modelo PRESERVANDO dados do usuário:
   * - Adiciona fases novas (no modelo, faltam aqui)
   * - Atualiza nome / etapa_macro / descrição / duração das fases existentes (template manda)
   * - Faz merge das etapas internas (preserva 'concluida' por nome da etapa)
   * - Por padrão remove fases órfãs (removidas do modelo); se removerOrfas=false, preserva
   */
  const sincronizarComModelo = async (removerOrfas = true) => {
    if (!cronograma || !selectedProject) return;
    const embarcacao = selectedProject.embarcacoes?.[selectedEmbarcacao];
    const modelo = vesselModels.find(m => m.id === embarcacao?.vessel_model_id);
    if (!modelo || !modelo.fases_aplicaveis?.length) {
      toast.error('Modelo da embarcação não possui fases aplicáveis');
      return;
    }

    // Mapa: fase_id → item existente (para preservar dados)
    const itensAtuaisMap = new Map();
    (cronograma.itens || []).forEach(i => { if (i.fase_id) itensAtuaisMap.set(i.fase_id, i); });

    // Ordenar fases do modelo conforme a ordem dos templates
    const fasesOrdenadas = processTemplates
      .filter(pt => modelo.fases_aplicaveis.includes(pt.id))
      .sort((a, b) => (a.etapa_macro - b.etapa_macro) || (a.order - b.order));

    const novosItens = fasesOrdenadas.map((fase, index) => {
      const existente = itensAtuaisMap.get(fase.id);

      // Merge etapas internas: chave = nome da etapa (mais robusto a re-IDs)
      const etapasExistentes = existente?.etapas || [];
      const etapasMergeadas = (fase.steps || []).map(step => {
        const jaTeve = etapasExistentes.find(e =>
          e.id === step.id || (e.nome || '').toLowerCase() === (step.name || '').toLowerCase()
        );
        return {
          id: step.id || jaTeve?.id || crypto.randomUUID(),
          nome: step.name,                                    // nome do modelo (atualizado)
          descricao: step.description || '',
          concluida: !!jaTeve?.concluida,
          data_conclusao: jaTeve?.data_conclusao || null,
        };
      });

      return {
        // ID interno do item: preserva se já existia
        id: existente?.id || crypto.randomUUID(),
        // Identificação da fase (sempre do modelo)
        fase_id: fase.id,
        fase_nome: fase.name,
        fase_descricao: fase.description || '',
        etapa_macro: fase.etapa_macro,
        etapa_macro_nome: fase.etapa_macro_nome,
        duracao_estimada_horas: fase.duration_hours || 0,
        etapas: etapasMergeadas,
        // Dados do usuário (preservados)
        fabrica_id: existente?.fabrica_id || '',
        fabrica_nome: existente?.fabrica_nome || '',
        data_inicio: existente?.data_inicio || '',
        data_fim: existente?.data_fim || '',
        status: existente?.status || 'planejado',
        progresso: existente?.progresso || 0,
        responsavel: existente?.responsavel || '',
        observacoes: existente?.observacoes || '',
        ordem: index + 1,
      };
    });

    // Decidir órfãs (no cronograma, removidas do modelo)
    if (!removerOrfas) {
      const orfas = (cronograma.itens || []).filter(i =>
        i.fase_id && !modelo.fases_aplicaveis.includes(i.fase_id)
      ).map(i => ({ ...i, orfa: true }));
      novosItens.push(...orfas);
    }

    const cronogramaAtualizado = { ...cronograma, itens: novosItens };
    try {
      const response = await api.post('/cronogramas', cronogramaAtualizado);
      setCronograma(response.data);
      lastSavedCronogramaRef.current = JSON.parse(JSON.stringify(response.data));
      setPendingChanges(false);
      toast.success('Cronograma sincronizado com o modelo. Dados preservados.');
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar cronograma');
    }
  };

  const handleSaveCronograma = async (cronogramaToSave = null, showToast = true) => {
    const dataToSave = cronogramaToSave || cronograma;
    if (!dataToSave) return;
    
    setSaving(true);
    try {
      await api.post('/cronogramas', dataToSave);
      lastSavedCronogramaRef.current = JSON.parse(JSON.stringify(dataToSave));
      setPendingChanges(false);
      if (showToast) {
        toast.success('Cronograma salvo com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar cronograma');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = useCallback((itemId, field, value) => {
    if (!cronograma) return;
    
    setCronograma(prev => ({
      ...prev,
      itens: prev.itens.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, [field]: value };
          
          // Se atualizar fábrica, atualizar nome também
          if (field === 'fabrica_id') {
            const fab = factories.find(f => f.id === value);
            updated.fabrica_nome = fab ? fab.name : '';
          }
          
          // Recalcular status se mudar datas
          if (field === 'data_inicio' || field === 'data_fim' || field === 'status') {
            updated.status = calculateStatus(updated);
          }
          
          return updated;
        }
        return item;
      })
    }));
    setPendingChanges(true);
  }, [cronograma, factories]);

  const updateEtapa = useCallback((itemId, etapaId, concluida) => {
    if (!cronograma) return;
    
    setCronograma(prev => ({
      ...prev,
      itens: prev.itens.map(item => {
        if (item.id === itemId) {
          const etapasAtualizadas = item.etapas.map(e => 
            e.id === etapaId ? { ...e, concluida } : e
          );
          
          // Calcular progresso baseado nas etapas
          const concluidas = etapasAtualizadas.filter(e => e.concluida).length;
          const progresso = etapasAtualizadas.length > 0 
            ? Math.round((concluidas / etapasAtualizadas.length) * 100)
            : 0;
          
          // Atualizar status automaticamente
          let novoStatus = item.status;
          if (progresso === 100) {
            novoStatus = 'concluido';
          } else if (progresso > 0 && novoStatus === 'planejado') {
            novoStatus = 'em_andamento';
          }
          
          return {
            ...item,
            etapas: etapasAtualizadas,
            progresso,
            status: novoStatus
          };
        }
        return item;
      })
    }));
    setPendingChanges(true);
  }, [cronograma]);

  const marcarFaseConcluida = useCallback(async (itemId) => {
    if (!cronograma) return;
    
    const updatedCronograma = {
      ...cronograma,
      itens: cronograma.itens.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            etapas: item.etapas.map(e => ({ ...e, concluida: true })),
            progresso: 100,
            status: 'concluido'
          };
        }
        return item;
      })
    };
    
    setCronograma(updatedCronograma);
    
    // Salvar automaticamente no banco
    try {
      await api.post('/cronogramas', updatedCronograma);
      toast.success('Fase marcada como concluída e salva!');
    } catch (error) {
      console.error('Erro ao salvar fase concluída:', error);
      toast.error('Erro ao salvar. Clique em Salvar manualmente.');
    }
  }, [cronograma]);

  const openItemEditor = (item) => {
    setEditingItem({ ...item });
    setShowItemDialog(true);
  };

  const saveItemEdit = async () => {
    if (!editingItem || !cronograma) return;
    
    const updatedItens = cronograma.itens.map(item => 
      item.id === editingItem.id ? { ...editingItem, status: calculateStatus(editingItem) } : item
    );
    
    const updatedCronograma = { ...cronograma, itens: updatedItens };
    setCronograma(updatedCronograma);
    setShowItemDialog(false);
    setEditingItem(null);
    
    // Salvar automaticamente no banco
    try {
      await api.post('/cronogramas', updatedCronograma);
      toast.success('Fase atualizada e salva!');
    } catch (error) {
      console.error('Erro ao salvar fase:', error);
      toast.error('Erro ao salvar. Clique em Salvar manualmente.');
    }
  };

  // Gerar dias para o calendário
  const calendarDays = useMemo(() => {
    const days = [];
    const current = new Date(viewStartDate);
    
    while (current <= viewEndDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  }, [viewStartDate, viewEndDate]);

  // Agrupar dias por semana/mês baseado no filtro
  const groupedDays = useMemo(() => {
    if (viewFilter === 'diario') {
      return calendarDays.map(day => ({
        label: formatDate(day),
        days: [day]
      }));
    }
    
    // Agrupar por semana
    const weeks = [];
    let currentWeek = [];
    
    calendarDays.forEach((day, idx) => {
      currentWeek.push(day);
      if (day.getDay() === 6 || idx === calendarDays.length - 1) {
        const weekStart = currentWeek[0];
        const weekEnd = currentWeek[currentWeek.length - 1];
        weeks.push({
          label: `${formatDate(weekStart)} - ${formatDate(weekEnd)}`,
          days: [...currentWeek]
        });
        currentWeek = [];
      }
    });
    
    return weeks;
  }, [calendarDays, viewFilter]);

  // Verificar se um item está em um período
  const isItemInPeriod = (item, periodDays) => {
    if (!item.data_inicio || !item.data_fim) return false;
    
    const itemStart = new Date(item.data_inicio);
    const itemEnd = new Date(item.data_fim);
    const periodStart = periodDays[0];
    const periodEnd = periodDays[periodDays.length - 1];
    
    return itemStart <= periodEnd && itemEnd >= periodStart;
  };

  // Calcular indicadores do projeto atual
  const indicadores = useMemo(() => {
    if (!cronograma?.itens) return { total: 0, planejado: 0, pendentePlanejamento: 0, emAndamento: 0, concluido: 0, atrasado: 0 };
    
    const itens = cronograma.itens.map(item => ({
      ...item,
      statusCalculado: calculateStatus(item)
    }));
    
    // Pendente de planejamento = status planejado E sem datas definidas
    const pendentePlanejamento = itens.filter(i => 
      i.statusCalculado === 'planejado' && (!i.data_inicio || !i.data_fim)
    ).length;
    
    // Planejado = status planejado E com datas definidas
    const planejado = itens.filter(i => 
      i.statusCalculado === 'planejado' && i.data_inicio && i.data_fim
    ).length;
    
    return {
      total: itens.length,
      planejado,
      pendentePlanejamento,
      emAndamento: itens.filter(i => i.statusCalculado === 'em_andamento').length,
      concluido: itens.filter(i => i.statusCalculado === 'concluido').length,
      atrasado: itens.filter(i => i.statusCalculado === 'atrasado').length
    };
  }, [cronograma]);

  // Calcular indicadores gerais de todos os projetos
  // Nota: Idealmente, verificaríamos cada cronograma, mas por performance
  // mostramos estatísticas baseadas no projeto atual
  const indicadoresGerais = useMemo(() => {
    // Para o projeto atual, verificar se 100% das fases estão planejadas
    const totalFases = cronograma?.itens?.length || 0;
    const fasesComDatas = cronograma?.itens?.filter(i => i.data_inicio && i.data_fim)?.length || 0;
    const projetoAtualPlanejado = totalFases > 0 && fasesComDatas === totalFases;
    
    return { 
      total: projects.length,
      comPlanejamento: projetoAtualPlanejado ? 1 : 0,
      semPlanejamento: projetoAtualPlanejado ? 0 : 1,
      percentualPlanejado: totalFases > 0 ? Math.round((fasesComDatas / totalFases) * 100) : 0
    };
  }, [projects, cronograma]);

  const progressoGeral = indicadores.total > 0 
    ? Math.round((indicadores.concluido / indicadores.total) * 100) 
    : 0;

  // Navegar no calendário
  const navigateCalendar = (direction) => {
    const days = FILTER_OPTIONS[viewFilter]?.days || 30;
    const newStart = new Date(viewStartDate);
    newStart.setDate(newStart.getDate() + (direction * days));
    
    const newEnd = new Date(newStart);
    newEnd.setDate(newStart.getDate() + days);
    
    setViewStartDate(newStart);
    setViewEndDate(newEnd);
  };

  const goToToday = () => {
    updateViewDates(viewFilter);
  };

  // Embarcação selecionada
  const embarcacaoAtual = selectedProject?.embarcacoes?.[selectedEmbarcacao];
  const modeloAtual = vesselModels.find(m => m.id === embarcacaoAtual?.vessel_model_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <TitleWithHelp
            title="Planejamento de Fases"
            description="Define QUANDO cada fase deve iniciar e terminar. As fases vêm do modelo de embarcação selecionado no projeto."
            connections={["Projetos", "Modelos"]}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarDays className="h-6 w-6 text-blue-600" />
              Cronograma de Produção
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Planejamento e acompanhamento por embarcação</p>
        </div>
        <div className="flex gap-2 items-center">
          {pendingChanges && (
            <span className="text-sm text-orange-500 flex items-center gap-1">
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
              Salvando...
            </span>
          )}
          {embarcacaoAtual && (
            <Button 
              variant="outline" 
              onClick={() => navigate(`/projects/${selectedProject.id}/embarcacao/${selectedEmbarcacao}`)}
            >
              <FileText className="mr-2 h-4 w-4" />
              Ver Especificação
            </Button>
          )}
          <Button 
            onClick={() => handleSaveCronograma()} 
            disabled={saving || !cronograma} 
            className={pendingChanges ? "bg-orange-500 hover:bg-orange-600" : "bg-blue-600 hover:bg-blue-700"}
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {saving ? 'Salvando...' : pendingChanges ? 'Salvar Agora' : 'Salvar'}
          </Button>
        </div>
      </div>

      {/* Dashboard Geral de Projetos */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="text-center px-4 border-r border-blue-200">
                <p className="text-2xl font-bold text-blue-700">{indicadoresGerais.total}</p>
                <p className="text-xs text-blue-600">Total Projetos</p>
              </div>
              <div className="text-center px-4 border-r border-blue-200">
                <p className="text-2xl font-bold text-green-600">{indicadoresGerais.percentualPlanejado}%</p>
                <p className="text-xs text-gray-600">Fases Planejadas</p>
                <p className="text-xs text-gray-400">(projeto atual)</p>
              </div>
              <div className="text-center px-4">
                {indicadoresGerais.percentualPlanejado === 100 ? (
                  <>
                    <div className="flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="text-sm font-medium text-green-600">Planejamento Completo</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-1">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                      <span className="text-sm font-medium text-orange-600">Pendente Planejamento</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {indicadores.pendentePlanejamento} fase(s) sem datas
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Seletores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Projeto</Label>
          <Select 
            value={selectedProject?.id || ''} 
            onValueChange={(value) => {
              const proj = projects.find(p => p.id === value);
              setSelectedProject(proj);
              setSelectedEmbarcacao(0);
              setCronograma(null);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o projeto" />
            </SelectTrigger>
            <SelectContent>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Embarcação</Label>
          <Select 
            value={selectedEmbarcacao.toString()} 
            onValueChange={(value) => {
              setSelectedEmbarcacao(parseInt(value));
              setCronograma(null);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a embarcação" />
            </SelectTrigger>
            <SelectContent>
              {selectedProject?.embarcacoes?.map((emb, idx) => (
                <SelectItem key={idx} value={idx.toString()}>
                  #{(idx + 1).toString().padStart(2, '0')} - {emb.vessel_model_nome || 'Sem modelo'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Código/SKU</Label>
          <div className="h-10 px-3 py-2 border rounded-md bg-gray-50 text-sm flex items-center gap-2">
            <Ship className="h-4 w-4 text-blue-600" />
            {modeloAtual?.codigo || 'Não definido'}
          </div>
        </div>
      </div>

      {/* Indicadores do Projeto Selecionado */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold">{progressoGeral}%</p>
              <p className="text-xs text-gray-500">Progresso</p>
              <Progress value={progressoGeral} className="mt-2 h-2" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-400">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{indicadores.planejado}</p>
              <p className="text-xs text-gray-500">Planejadas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-400">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-500">{indicadores.pendentePlanejamento}</p>
              <p className="text-xs text-gray-500">Pendente Plan.</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-cyan-600">{indicadores.emAndamento}</p>
              <p className="text-xs text-gray-500">Em Andamento</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{indicadores.concluido}</p>
              <p className="text-xs text-gray-500">Concluídas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{indicadores.atrasado}</p>
              <p className="text-xs text-gray-500">Em Atraso</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros de Visualização */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Tabs value={viewFilter} onValueChange={setViewFilter}>
                <TabsList>
                  <TabsTrigger value="diario">Diário</TabsTrigger>
                  <TabsTrigger value="semanal">Semanal</TabsTrigger>
                  <TabsTrigger value="mensal">Mensal</TabsTrigger>
                  <TabsTrigger value="personalizado">Personalizado</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            {viewFilter === 'personalizado' && (
              <div className="flex items-center gap-2">
                <Input 
                  type="date" 
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                  className="w-36"
                />
                <span className="text-gray-500">até</span>
                <Input 
                  type="date" 
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                  className="w-36"
                />
                <Button size="sm" onClick={handleCustomDateChange}>Aplicar</Button>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigateCalendar(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                <CalendarDays className="h-4 w-4 mr-1" />
                Hoje
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigateCalendar(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Fases / Cronograma */}
      {!cronograma ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="mx-auto h-12 w-12 text-gray-400 animate-spin mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Carregando cronograma...
            </h3>
          </CardContent>
        </Card>
      ) : cronograma.itens?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhuma fase definida
            </h3>
            <p className="text-gray-500 mb-4">
              O modelo de embarcação não possui fases configuradas
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Banner de sincronização com modelo da embarcação */}
          {diffCronogramaModelo?.precisaSincronizar && (
            <Card className="border-orange-300 bg-orange-50" data-testid="banner-sync-cronograma">
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-2 flex-1 min-w-[280px]">
                    <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                    <div className="text-sm text-orange-800">
                      <p className="font-semibold mb-1">Cronograma fora de sincronia com o modelo da embarcação</p>
                      <ul className="space-y-0.5 text-xs">
                        {diffCronogramaModelo.fasesNovas.length > 0 && (
                          <li>
                            <strong>{diffCronogramaModelo.fasesNovas.length}</strong> fase(s) <strong>nova(s)</strong> no modelo que ainda não estão no cronograma:
                            <span className="ml-1 italic">
                              {diffCronogramaModelo.fasesNovas.slice(0, 3).map(f => f.name).join(', ')}
                              {diffCronogramaModelo.fasesNovas.length > 3 && '...'}
                            </span>
                          </li>
                        )}
                        {diffCronogramaModelo.fasesOrfas.length > 0 && (
                          <li>
                            <strong>{diffCronogramaModelo.fasesOrfas.length}</strong> fase(s) no cronograma que foram <strong>removidas</strong> do modelo:
                            <span className="ml-1 italic">
                              {diffCronogramaModelo.fasesOrfas.slice(0, 3).map(f => f.fase_nome).join(', ')}
                              {diffCronogramaModelo.fasesOrfas.length > 3 && '...'}
                            </span>
                          </li>
                        )}
                        {diffCronogramaModelo.fasesDessincronizadas.length > 0 && (
                          <li>
                            <strong>{diffCronogramaModelo.fasesDessincronizadas.length}</strong> fase(s) com <strong>nome/etapa</strong> diferente(s) do modelo
                          </li>
                        )}
                      </ul>
                      <p className="text-[11px] text-orange-700 mt-1.5 italic">
                        Sincronizar preserva datas, status, progresso, responsável e fábricas já preenchidos.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button
                      size="sm"
                      className="bg-orange-600 hover:bg-orange-700"
                      onClick={() => {
                        const msg = diffCronogramaModelo.fasesOrfas.length > 0
                          ? `Sincronizar removendo ${diffCronogramaModelo.fasesOrfas.length} fase(s) órfã(s) e atualizando ${diffCronogramaModelo.fasesNovas.length + diffCronogramaModelo.fasesDessincronizadas.length} fase(s)?\n\nDatas e status preenchidos serão preservados.`
                          : `Sincronizar adicionando ${diffCronogramaModelo.fasesNovas.length} fase(s) nova(s) e atualizando ${diffCronogramaModelo.fasesDessincronizadas.length} fase(s)?\n\nDados preenchidos serão preservados.`;
                        if (window.confirm(msg)) sincronizarComModelo(true);
                      }}
                      data-testid="btn-sincronizar-modelo"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Sincronizar
                    </Button>
                    {diffCronogramaModelo.fasesOrfas.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-orange-400 text-orange-700 hover:bg-orange-100"
                        onClick={() => {
                          if (window.confirm('Sincronizar mantendo as fases órfãs (que não estão mais no modelo)? Elas serão marcadas como "órfã".')) {
                            sincronizarComModelo(false);
                          }
                        }}
                        data-testid="btn-sincronizar-preservar-orfas"
                      >
                        Sincronizar (preservar órfãs)
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Agrupar por Etapa Macro - detecta dinamicamente TODOS os números presentes no cronograma */}
          {(() => {
            const macrosPresentes = Array.from(new Set(
              cronograma.itens.map(i => i.etapa_macro).filter(v => v !== null && v !== undefined)
            ));
            return [
              ...macrosPresentes.sort((a, b) => {
                const ordA = etapasMacro.find(e => e.numero === a)?.ordem ?? a;
                const ordB = etapasMacro.find(e => e.numero === b)?.ordem ?? b;
                return ordA - ordB;
              }),
              'none'
            ];
          })().map(etapaMacro => {
            const itensDaEtapa = cronograma.itens
              .filter(i => 
                etapaMacro === 'none' 
                  ? (!i.etapa_macro || i.etapa_macro === null || i.etapa_macro === undefined)
                  : i.etapa_macro === etapaMacro
              )
              .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
            if (itensDaEtapa.length === 0) return null;
            
            // Buscar nome da etapa macro da lista carregada (fonte principal)
            const etapaMacroData = etapasMacro.find(e => e.numero === etapaMacro);
            const etapaNome = etapaMacro === 'none' 
              ? 'Fases (sem etapa definida)' 
              : (etapaMacroData?.nome || itensDaEtapa[0]?.etapa_macro_nome || `Etapa ${etapaMacro}`);
            const etapaKey = etapaMacro;
            
            return (
              <Card key={etapaKey}>
                <CardHeader className="py-3 bg-gray-50">
                  <CardTitle className="text-base flex items-center gap-2">
                    {etapaMacro !== 'none' && (
                      <Badge variant="outline">{etapaMacro}</Badge>
                    )}
                    {etapaNome}
                    <span className="text-sm font-normal text-gray-500">
                      ({itensDaEtapa.length} fases)
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {itensDaEtapa.map((item) => {
                      const statusCalculado = calculateStatus(item);
                      const StatusIcon = STATUS_CONFIG[statusCalculado]?.icon || Clock;
                      
                      return (
                        <div 
                          key={item.id} 
                          className="p-4 hover:bg-gray-50 cursor-pointer"
                          onClick={() => openItemEditor(item)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                {item.ordem !== undefined && item.ordem !== null && (
                                  <span className="text-xs font-semibold text-gray-400" data-testid={`item-ordem-${item.id}`}>
                                    #{item.ordem}
                                  </span>
                                )}
                                <span className="font-medium">{item.fase_nome}</span>
                                <Badge className={`text-xs ${STATUS_CONFIG[statusCalculado]?.color}`}>
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {STATUS_CONFIG[statusCalculado]?.label}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-500">Fábrica:</span>
                                  <Select
                                    value={item.fabrica_id || 'none'}
                                    onValueChange={(value) => {
                                      updateItem(item.id, 'fabrica_id', value === 'none' ? '' : value);
                                    }}
                                  >
                                    <SelectTrigger className="h-8 mt-1" onClick={(e) => e.stopPropagation()}>
                                      <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">Não definida</SelectItem>
                                      {factories.length === 0 ? (
                                        <SelectItem value="sem-fabricas" disabled>Nenhuma fábrica cadastrada</SelectItem>
                                      ) : (
                                        factories.map(f => (
                                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                                        ))
                                      )}
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div>
                                  <span className="text-gray-500">Prev. Início:</span>
                                  <Input 
                                    type="date" 
                                    value={item.data_previsao_inicio || item.data_inicio || ''}
                                    onChange={(e) => {
                                      updateItem(item.id, 'data_previsao_inicio', e.target.value);
                                      updateItem(item.id, 'data_inicio', e.target.value);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-8 mt-1"
                                  />
                                </div>
                                
                                <div>
                                  <span className="text-gray-500">Prev. Fim:</span>
                                  <Input 
                                    type="date" 
                                    value={item.data_previsao_fim || item.data_fim || ''}
                                    onChange={(e) => {
                                      updateItem(item.id, 'data_previsao_fim', e.target.value);
                                      updateItem(item.id, 'data_fim', e.target.value);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-8 mt-1"
                                  />
                                </div>
                                
                                <div>
                                  <span className="text-gray-500">Progresso:</span>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Progress value={item.progresso || 0} className="h-2 flex-1" />
                                    <span className="text-xs font-medium">{item.progresso || 0}%</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-col gap-1">
                              {statusCalculado !== 'concluido' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    marcarFaseConcluida(item.id);
                                  }}
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-1" />
                                  Concluir
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog de Edição de Item */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {editingItem?.fase_nome}
              <HelpTooltip 
                title="Planejamento da Fase"
                description="Configure os detalhes desta fase: defina fábrica responsável, datas planejadas e reais, status e acompanhe as etapas micro (checklist interno da fase)."
                connections={['Produção', 'Fábricas']}
                iconSize={16}
              />
            </DialogTitle>
          </DialogHeader>
          
          {editingItem && (
            <div className="space-y-4">
              {/* Informações da Fase */}
              {editingItem.fase_descricao && (
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{editingItem.fase_descricao}</p>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fábrica</Label>
                  <Select
                    value={editingItem.fabrica_id || 'none'}
                    onValueChange={(value) => {
                      const fab = factories.find(f => f.id === value);
                      setEditingItem({ 
                        ...editingItem, 
                        fabrica_id: value === 'none' ? '' : value,
                        fabrica_nome: fab ? fab.name : ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Não definida</SelectItem>
                      {factories.length === 0 ? (
                        <SelectItem value="sem-fabricas" disabled>Nenhuma fábrica cadastrada</SelectItem>
                      ) : (
                        factories.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Responsável</Label>
                  <Input
                    value={editingItem.responsavel || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, responsavel: e.target.value })}
                    placeholder="Nome do responsável"
                  />
                </div>
              </div>
              
              {/* Datas Planejadas */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-xs text-gray-500 mb-2 block">Datas Planejadas</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Previsão Início</Label>
                    <Input
                      type="date"
                      value={editingItem.data_previsao_inicio || editingItem.data_inicio || ''}
                      onChange={(e) => setEditingItem({ 
                        ...editingItem, 
                        data_previsao_inicio: e.target.value,
                        data_inicio: e.target.value 
                      })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Previsão Fim</Label>
                    <Input
                      type="date"
                      value={editingItem.data_previsao_fim || editingItem.data_fim || ''}
                      onChange={(e) => setEditingItem({ 
                        ...editingItem, 
                        data_previsao_fim: e.target.value,
                        data_fim: e.target.value 
                      })}
                    />
                  </div>
                </div>
              </div>
              
              {/* Datas Reais */}
              <div className="p-3 bg-green-50 rounded-lg">
                <Label className="text-xs text-green-600 mb-2 block">Execução Real (preenchido pela Produção)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Início Real</Label>
                    <Input
                      type="date"
                      value={editingItem.data_inicio_real?.split('T')[0] || ''}
                      onChange={(e) => setEditingItem({ 
                        ...editingItem, 
                        data_inicio_real: e.target.value ? new Date(e.target.value).toISOString() : null
                      })}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Fim Real</Label>
                    <Input
                      type="date"
                      value={editingItem.data_fim_real?.split('T')[0] || ''}
                      onChange={(e) => setEditingItem({ 
                        ...editingItem, 
                        data_fim_real: e.target.value ? new Date(e.target.value).toISOString() : null
                      })}
                      className="bg-white"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editingItem.status}
                    onValueChange={(value) => setEditingItem({ ...editingItem, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Duração Estimada (horas)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={editingItem.duracao_estimada_horas || 0}
                    onChange={(e) => setEditingItem({ ...editingItem, duracao_estimada_horas: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>
              
              {/* Etapas (Micro) */}
              {editingItem.etapas?.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Etapas ({editingItem.etapas.filter(e => e.concluida).length}/{editingItem.etapas.length})</Label>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        const todasConcluidas = editingItem.etapas.every(e => e.concluida);
                        setEditingItem({
                          ...editingItem,
                          etapas: editingItem.etapas.map(e => ({ ...e, concluida: !todasConcluidas })),
                          progresso: todasConcluidas ? 0 : 100,
                          status: todasConcluidas ? 'planejado' : 'concluido'
                        });
                      }}
                    >
                      {editingItem.etapas.every(e => e.concluida) ? 'Desmarcar Todas' : 'Marcar Todas'}
                    </Button>
                  </div>
                  <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                    {editingItem.etapas.map((etapa, idx) => (
                      <div 
                        key={etapa.id} 
                        className={`p-2 flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 ${etapa.concluida ? 'bg-green-50' : ''}`}
                        onClick={() => {
                          const etapasAtualizadas = editingItem.etapas.map((e, i) => 
                            i === idx ? { ...e, concluida: !e.concluida } : e
                          );
                          const concluidas = etapasAtualizadas.filter(e => e.concluida).length;
                          const progresso = Math.round((concluidas / etapasAtualizadas.length) * 100);
                          
                          setEditingItem({
                            ...editingItem,
                            etapas: etapasAtualizadas,
                            progresso,
                            status: progresso === 100 ? 'concluido' : (progresso > 0 ? 'em_andamento' : 'planejado')
                          });
                        }}
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${etapa.concluida ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'}`}>
                          {etapa.concluida && <CheckCircle2 className="h-4 w-4" />}
                        </div>
                        <span className={etapa.concluida ? 'line-through text-gray-400' : ''}>{etapa.nome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea
                  value={editingItem.observacoes || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, observacoes: e.target.value })}
                  placeholder="Observações sobre esta fase..."
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancelar</Button>
            <Button onClick={saveItemEdit} className="bg-blue-600 hover:bg-blue-700">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cronograma;
